import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  ArrowLeft, ClipboardList, Copy, CopyPlus, Eye, FileUp, Loader2, Plus, Sparkles, Trash2, X,
} from 'lucide-react';
import { WorksheetFieldInput } from '@/components/worksheets/WorksheetFieldInput';
import { answerText } from '@/components/worksheets/answerText';
import { TableFieldEditor } from '@/components/admin/TableFieldEditor';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const FIELD_TYPES = [
  { value: 'short_text', label: 'Short answer' },
  { value: 'paragraph', label: 'Long answer' },
  { value: 'checkbox', label: 'Tick list' },
  { value: 'multiple_choice', label: 'Pick one' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'scale', label: 'Rating scale' },
  { value: 'date', label: 'Date' },
  { value: 'list', label: 'Numbered list' },
  { value: 'signature', label: 'Signature' },
  { value: 'table_fill', label: 'Table (they fill it in)' },
  { value: 'table', label: 'Table (for them to read)' },
  { value: 'heading', label: 'Section heading' },
  { value: 'instruction', label: 'Your words (nothing to fill)' },
];

const CHOICE_TYPES = ['checkbox', 'multiple_choice', 'dropdown'];
const TABLE_TYPES = ['table', 'table_fill'];

/** Ask after a conversion until it is done. Gives up after ten minutes, not silently. */
const waitForConversion = async (jobId) => {
  const deadline = Date.now() + 10 * 60 * 1000;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 3000));
    const { data } = await axios.get(`${API}/intake-forms/drafter/job/${jobId}`, auth());
    if (data.status === 'done') return data;
    if (data.status === 'failed') throw new Error(data.detail || 'Could not convert that PDF');
  }
  throw new Error('That conversion is taking unusually long. Check back in a minute — if the '
                  + 'form appears in your list it finished.');
};

/** Deep copies of fields, each with its own id — options, table columns and flags included. */
const cloneFields = (fields) => (fields || []).map((f) => ({
  ...JSON.parse(JSON.stringify(f)), id: `f_${Math.random().toString(36).slice(2, 9)}`,
}));

const blankField = (type = 'short_text') => ({
  id: `f_${Math.random().toString(36).slice(2, 9)}`,
  type,
  label: '',
  help: '',
  required: false,
  journal_prompt: false,
  keep_in_journal: false,
  options: CHOICE_TYPES.includes(type) ? ['', ''] : [],
  allow_other: false,
  scale_min: 1,
  scale_max: 5,
  min_label: '',
  max_label: '',
  rows: 4,
  list_count: 3,
  table_columns: [],
  table_rows: [],
  table_column_types: [],
  table_column_options: [],
  table_start_rows: 3,
});

/** One question, as the practitioner edits it. */
const FieldRow = ({ field, index, onChange, onRemove, onDuplicate, onDuplicateBlock }) => {
  const set = (patch) => onChange({ ...field, ...patch });
  const isChoice = CHOICE_TYPES.includes(field.type);
  const isTable = TABLE_TYPES.includes(field.type);
  const isDisplay = field.type === 'heading' || field.type === 'instruction'
    || field.type === 'table';

  return (
    // text-gray-900 is not decoration: this row is the only part of the builder outside a
    // shadcn Card, so it inherits the Practitioner Hub's cream `.ph-root` colour and every
    // input renders light-on-white. Standing directive: never light text on a light surface.
    <div className="border rounded-lg p-4 space-y-3 bg-white text-gray-900" data-testid={`intake-field-${index}`}>
      <div className="flex items-start gap-2">
        <select
          value={field.type}
          onChange={(e) => set({
            type: e.target.value,
            options: CHOICE_TYPES.includes(e.target.value) && !field.options?.length
              ? ['', ''] : field.options,
            // A table with no columns has nothing to show, so it starts with two.
            table_columns: TABLE_TYPES.includes(e.target.value) && !field.table_columns?.length
              ? ['', ''] : field.table_columns,
          })}
          className="text-xs border rounded px-2 py-1.5 bg-white text-gray-900"
          data-testid={`intake-field-type-${index}`}
        >
          {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <div className="flex-1" />
        {field.type === 'heading' && (
          <button onClick={onDuplicateBlock}
                  className="text-gray-600 hover:text-cyan-600 transition-colors duration-200"
                  title="Copy this heading and every question under it"
                  aria-label="Copy this heading and every question under it"
                  data-testid={`intake-field-duplicate-block-${index}`}>
            <CopyPlus className="w-4 h-4" />
          </button>
        )}
        <button onClick={onDuplicate}
                className="text-gray-600 hover:text-cyan-600 transition-colors duration-200"
                title="Duplicate this question"
                aria-label="Duplicate this question"
                data-testid={`intake-field-duplicate-${index}`}>
          <Copy className="w-4 h-4" />
        </button>
        <button onClick={onRemove} className="text-gray-600 hover:text-rose-500 transition-colors duration-200"
                aria-label="Delete this question"
                data-testid={`intake-field-remove-${index}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {field.type === 'instruction' ? (
        <Textarea
          rows={3}
          value={field.label}
          onChange={(e) => set({ label: e.target.value })}
          placeholder="Anything they should read here — a policy, how to prepare…"
          data-testid={`intake-field-label-${index}`}
        />
      ) : (
        <Input
          value={field.label}
          onChange={(e) => set({ label: e.target.value })}
          placeholder={field.type === 'heading' ? 'Section heading' : 'Your question'}
          data-testid={`intake-field-label-${index}`}
        />
      )}

      {!isDisplay && (
        <Input
          value={field.help}
          onChange={(e) => set({ help: e.target.value })}
          placeholder="Hint underneath (optional)"
          className="text-sm"
          data-testid={`intake-field-help-${index}`}
        />
      )}

      {isChoice && (
        <div className="space-y-2">
          {(field.options || []).map((opt, oi) => (
            <div key={oi} className="flex gap-2 items-center">
              <Input
                value={opt}
                onChange={(e) => set({
                  options: field.options.map((o, i) => (i === oi ? e.target.value : o)),
                })}
                placeholder={`Option ${oi + 1}`}
                className="text-sm"
                data-testid={`intake-field-option-${index}-${oi}`}
              />
              <button onClick={() => set({ options: field.options.filter((_, i) => i !== oi) })}
                      className="text-gray-400 hover:text-rose-500 transition-colors duration-200">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <Button size="sm" variant="outline"
                  onClick={() => set({ options: [...(field.options || []), ''] })}
                  data-testid={`intake-field-add-option-${index}`}>
            <Plus className="w-3 h-3 mr-1" /> Option
          </Button>
        </div>
      )}

      {isTable && (
        // The admin table editor, reused rather than rebuilt. Its inputs are dark by design,
        // so it sits on a dark panel here too — light text on a light card is never allowed.
        <div className="rounded-lg bg-[#12161c] border border-white/10 p-3">
          <p className="text-[11px] text-gray-400 mb-2">
            {field.type === 'table_fill'
              ? 'Name each column and say what it takes. Your client fills the rows in.'
              : 'A table for them to read — fill in the cells yourself.'}
          </p>
          <TableFieldEditor field={field} index={index} onChange={(patch) => set(patch)} />
        </div>
      )}

      {field.type === 'signature' && (
        <p className="text-xs text-gray-500" data-testid={`intake-field-signature-note-${index}`}>
          They type their full name and tick to agree. The date and time are recorded with it and
          appear on the printed copy.
        </p>
      )}

      {field.type === 'scale' && (
        <div className="flex gap-3 items-center text-sm">
          <span className="text-gray-500">From</span>
          <Input type="number" value={field.scale_min} className="w-16"
                 onChange={(e) => set({ scale_min: Number(e.target.value) })} />
          <span className="text-gray-500">to</span>
          <Input type="number" value={field.scale_max} className="w-16"
                 onChange={(e) => set({ scale_max: Number(e.target.value) })} />
        </div>
      )}

      {!isDisplay && (
        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
          <input type="checkbox" checked={!!field.required}
                 onChange={(e) => set({ required: e.target.checked })}
                 data-testid={`intake-field-required-${index}`} />
          They can&apos;t send the form without answering this
        </label>
      )}
    </div>
  );
};

export default function IntakeFormsPage() {
  const [forms, setForms] = useState([]);
  const [allowance, setAllowance] = useState(null);
  const [editing, setEditing] = useState(null);
  const [responses, setResponses] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertNote, setConvertNote] = useState('');
  const [preview, setPreview] = useState(false);
  const [answers, setAnswers] = useState({});
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/intake-forms/mine`, auth());
      setForms(r.data.forms || []);
      setAllowance(r.data.allowance);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load your forms');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({
    title: '', intro: '', fields: [blankField()], timing: 'before', required: false,
    published: false, ask_once: false, once_scope: 'client',
  });

  const save = async () => {
    if (!editing.title.trim()) return toast.error('Give the form a name first');
    setSaving(true);
    try {
      const body = {
        title: editing.title,
        intro: editing.intro,
        fields: editing.fields,
        timing: editing.timing,
        required: editing.required,
        published: editing.published,
        ask_once: !!editing.ask_once,
        once_scope: editing.once_scope || 'client',
      };
      if (editing.id) {
        await axios.patch(`${API}/intake-forms/${editing.id}`, body, auth());
      } else {
        await axios.post(`${API}/intake-forms`, body, auth());
      }
      toast.success(editing.published ? 'Saved — still Live' : 'Saved as a draft — flip the Live switch on its card when you are ready');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save that');
    } finally {
      setSaving(false);
    }
  };

  // Publishing lives on the card because the checkbox inside the editor was too easy to miss
  // (owner, Jun 2026) — a form stays a draft and never appears on Session Types.
  const togglePublished = async (form, next) => {
    // Unpublishing stops the form reaching clients, silently — so say what it is wired into
    // and name the offerings before she does it.
    if (!next && (form.used_by || []).length) {
      const where = form.used_by.join(', ');
      const ok = window.confirm(
        `"${form.title}" is attached to ${form.used_by.length === 1 ? '' : form.used_by.length + ' offerings: '}`
        + `${where}.\n\nTake it back to a draft and clients booking those will no longer receive it. `
        + `Their existing bookings keep the form they were already given.\n\nUnpublish it anyway?`
      );
      if (!ok) return;
    }
    setForms((prev) => prev.map((f) => (f.id === form.id ? { ...f, published: next } : f)));
    try {
      await axios.patch(`${API}/intake-forms/${form.id}`, { published: next }, auth());
      toast.success(next ? 'Live — you can attach it to a session now' : 'Back to a draft');
    } catch (e) {
      setForms((prev) => prev.map((f) => (f.id === form.id ? { ...f, published: !next } : f)));
      toast.error(e?.response?.data?.detail || 'Could not change that');
    }
  };

  const remove = async (form) => {
    if (!window.confirm(`Delete "${form.title}"?`)) return;
    try {
      await axios.delete(`${API}/intake-forms/${form.id}`, auth());
      toast.success('Form deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete that');
    }
  };

  const convert = async (file) => {
    if (!file) return;
    setConverting(true);
    setConvertNote('Reading your form…');
    try {
      const body = new FormData();
      body.append('file', file);
      const started = await axios.post(`${API}/intake-forms/drafter/from-pdf`, body, auth());
      // The conversion runs behind the request now — a 20-page form takes minutes, far longer
      // than the gateway will hold a connection open, which is what "an invalid or incomplete
      // response" meant (owner, Jun 2026). Ask after the job until it is finished.
      const { job_id: jobId, pages } = started.data;
      setConvertNote(pages > 6
        ? `Reading all ${pages} pages — a long form takes a few minutes. You can stay here.`
        : 'Reading your form — this takes a moment.');
      const done = await waitForConversion(jobId);
      setAllowance(done.allowance);
      setEditing(done.form);
      const notes = done.notes || [];
      toast.success(notes.length
        ? 'Converted — read the notes at the top before you publish it'
        : 'Converted — check it over and publish when you are happy');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not convert that PDF');
    } finally {
      setConverting(false);
      setConvertNote('');
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const openResponses = async (form) => {
    try {
      const r = await axios.get(`${API}/intake-forms/responses/list?form_id=${form.id}`, auth());
      setResponses({ form, list: r.data.responses || [] });
      // Reading them is what clears the badge.
      setForms((prev) => prev.map((f) => (f.id === form.id ? { ...f, unread_count: 0 } : f)));
    } catch {
      toast.error('Could not load the answers');
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex justify-center" data-testid="intake-forms-loading">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  // ── reading what a client sent back ──
  if (responses) {
    return (
      <div className="max-w-3xl mx-auto space-y-5" data-testid="intake-responses-view">
        <Button variant="ghost" onClick={() => setResponses(null)} data-testid="intake-responses-back">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to forms
        </Button>
        <h1 className="text-lg md:text-xl font-semibold">{responses.form.title}</h1>
        {responses.list.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-sm text-gray-500">
            Nobody has filled this in yet.
          </CardContent></Card>
        ) : responses.list.map((res) => (
          <Card key={res.id} data-testid={`intake-response-${res.id}`}>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-baseline justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">{res.client_name || res.client_email || 'A client'}</p>
                  <p className="text-xs text-gray-500">
                    {res.session_title ? `${res.session_title} · ` : ''}
                    sent {(res.submitted_at || '').slice(0, 16).replace('T', ' ')}
                  </p>
                </div>
                <Badge variant="outline">{res.timing === 'after' ? 'After session' : 'Before session'}</Badge>
              </div>
              <div className="divide-y">
                {(res.fields_snapshot || []).filter((f) => !['heading', 'instruction'].includes(f.type))
                  .map((f) => {
                    const text = answerText(f, res.answers?.[f.id]);
                    return (
                      <div key={f.id} className="py-2">
                        <p className="text-xs text-gray-500">{f.label}</p>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">
                          {text || <span className="text-gray-400">—</span>}
                        </p>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // ── building a form ──
  if (editing) {
    return (
      <div className="max-w-3xl mx-auto space-y-5" data-testid="intake-form-editor">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => setEditing(null)} data-testid="intake-editor-back">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={() => { setPreview(!preview); setAnswers({}); }}
                  data-testid="intake-editor-preview-btn">
            <Eye className="w-4 h-4 mr-2" /> {preview ? 'Keep editing' : 'Preview'}
          </Button>
          <Button onClick={save} disabled={saving} data-testid="intake-editor-save-btn">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>

        {(editing.drafter_notes || []).length > 0 && (
          <Card className="border-amber-300 bg-amber-50" data-testid="intake-drafter-notes">
            <CardContent className="p-4">
              <p className="text-sm font-medium text-amber-900 mb-1">
                Worth a look before you publish
              </p>
              <ul className="list-disc pl-5 text-sm text-amber-800 space-y-1">
                {editing.drafter_notes.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-5 space-y-4">
            <Input
              value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              placeholder="Form name — e.g. New Client Health History"
              className="text-base font-medium"
              data-testid="intake-editor-title"
            />
            <Textarea
              rows={3}
              value={editing.intro}
              onChange={(e) => setEditing({ ...editing, intro: e.target.value })}
              placeholder="Anything they should read before they start (optional)"
              data-testid="intake-editor-intro"
            />
            <div className="flex flex-wrap gap-5 items-center text-sm">
              <label className="flex items-center gap-2">
                <span className="text-gray-600">When</span>
                <select
                  value={editing.timing}
                  onChange={(e) => setEditing({ ...editing, timing: e.target.value })}
                  className="border rounded px-2 py-1.5 bg-white text-gray-900"
                  data-testid="intake-editor-timing"
                >
                  <option value="before">Before the session</option>
                  <option value="after">After the session</option>
                </select>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!editing.required}
                       onChange={(e) => setEditing({ ...editing, required: e.target.checked })}
                       data-testid="intake-editor-required" />
                <span className="text-gray-600">Required — remind them if it&apos;s outstanding</span>
              </label>
            </div>

            {/* A waiver is signed the first time and then left alone. Not a timing option: it
                still goes out before the session — this is about whether it is owed again. */}
            <div className="rounded-lg border bg-stone-50 p-3 space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" className="mt-0.5" checked={!!editing.ask_once}
                       onChange={(e) => setEditing({ ...editing, ask_once: e.target.checked })}
                       data-testid="intake-editor-ask-once" />
                <span className="text-sm text-gray-700">
                  Ask for this <strong>once</strong> — for a waiver or consent they sign the first
                  time and shouldn&apos;t have to sign again
                </span>
              </label>
              {editing.ask_once && (
                <div className="pl-6 space-y-1.5">
                  {[
                    ['client', 'Once per client — the first time I ever see them'],
                    ['session_type', 'Once per session type — the first time they book that particular session'],
                  ].map(([value, text]) => (
                    <label key={value} className="flex items-start gap-2 cursor-pointer">
                      <input type="radio" name="once_scope" className="mt-0.5" value={value}
                             checked={(editing.once_scope || 'client') === value}
                             onChange={() => setEditing({ ...editing, once_scope: value })}
                             data-testid={`intake-editor-once-scope-${value}`} />
                      <span className="text-sm text-gray-600">{text}</span>
                    </label>
                  ))}
                  <p className="text-xs text-gray-400">
                    If we can&apos;t tell who they are, they&apos;re asked again — being asked
                    twice is better than never being asked.
                  </p>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-400" data-testid="intake-editor-publish-hint">
              Save it, then use the <strong>Live</strong> switch on the form&apos;s card to make it
              available on your session types.
            </p>
          </CardContent>
        </Card>

        {preview ? (
          <Card data-testid="intake-editor-preview">
            <CardContent className="p-5 space-y-5">
              <p className="text-xs uppercase tracking-wide text-gray-400">
                What your client sees
              </p>
              {(editing.fields || []).map((f) => (
                <WorksheetFieldInput
                  key={f.id}
                  field={f}
                  value={answers[f.id]}
                  onChange={(v) => setAnswers({ ...answers, [f.id]: v })}
                />
              ))}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {(editing.fields || []).map((f, i) => (
              <FieldRow
                key={f.id}
                field={f}
                index={i}
                onChange={(next) => setEditing({
                  ...editing,
                  fields: editing.fields.map((x, xi) => (xi === i ? next : x)),
                })}
                onRemove={() => setEditing({
                  ...editing, fields: editing.fields.filter((_, xi) => xi !== i),
                })}
                onDuplicate={() => {
                  const [copy] = cloneFields([f]);
                  const fields = [...editing.fields];
                  fields.splice(i + 1, 0, copy);
                  setEditing({ ...editing, fields });
                  toast.success('Copied — the duplicate is just below');
                }}
                onDuplicateBlock={() => {
                  const rest = editing.fields.slice(i + 1);
                  const nextHeading = rest.findIndex((x) => x.type === 'heading');
                  const end = nextHeading === -1 ? editing.fields.length : i + 1 + nextHeading;
                  const copies = cloneFields(editing.fields.slice(i, end));
                  const fields = [...editing.fields];
                  fields.splice(end, 0, ...copies);
                  setEditing({ ...editing, fields });
                  toast.success(`Section copied — the heading and ${copies.length - 1} question`
                                + `${copies.length === 2 ? '' : 's'} under it`);
                }}
              />
            ))}
            <Button variant="outline"
                    onClick={() => setEditing({ ...editing, fields: [...editing.fields, blankField()] })}
                    data-testid="intake-editor-add-field">
              <Plus className="w-4 h-4 mr-2" /> Add a question
            </Button>
          </div>
        )}
      </div>
    );
  }

  // ── her list of forms ──
  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="intake-forms-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ClipboardList className="w-5 h-5 text-cyan-600" />
            <h1 className="text-lg md:text-xl font-semibold text-[#f5f0e8]">Your client forms</h1>
          </div>
          <p className="text-sm text-gray-500 max-w-xl">
            Build a form here and attach it to any session you offer. Your clients fill it in on
            the site — no account needed — and the answers come back to you.
          </p>
        </div>
        <div className="flex gap-2">
          <input ref={fileInput} type="file" accept=".pdf" hidden
                 onChange={(e) => convert(e.target.files?.[0])}
                 data-testid="intake-convert-file-input" />
          <Button variant="outline" onClick={() => fileInput.current?.click()}
                  disabled={converting || (allowance && allowance.remaining <= 0)}
                  data-testid="intake-convert-btn">
            {converting
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Converting…</>
              : <><FileUp className="w-4 h-4 mr-2" /> Convert a PDF</>}
          </Button>
          <Button onClick={startNew} data-testid="intake-new-form-btn">
            <Plus className="w-4 h-4 mr-2" /> New form
          </Button>
        </div>
      </div>

      {converting && convertNote && (
        <p className="text-xs text-cyan-600 flex items-center gap-1.5" data-testid="intake-convert-status">
          <Loader2 className="w-3 h-3 animate-spin" /> {convertNote}
        </p>
      )}

      {allowance && (
        <p className="text-xs text-gray-400 flex items-center gap-1.5" data-testid="intake-allowance">
          <Sparkles className="w-3 h-3" />
          {allowance.remaining > 0
            ? `${allowance.remaining} of ${allowance.cap} PDF conversions left. Building by hand is unlimited.`
            : `You've used all ${allowance.cap} PDF conversions — building by hand is still unlimited.`}
        </p>
      )}

      {/* Said once, quietly, where a form is built. Both she and her client need to know this
          isn't a medical records system — the client-facing version rides on the email that
          carries the form (owner, Jun 2026). */}
      <p className="text-xs text-gray-400" data-testid="intake-hipaa-notice">
        This is not a HIPAA-compliant system. Please don&rsquo;t collect protected health
        information here if you are a covered entity, and check your own obligations before
        asking clients for medical detail.
      </p>

      {forms.length === 0 ? (
        <Card><CardContent className="p-10 text-center">
          <ClipboardList className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">
            No forms yet. Build one, or convert a PDF you already use.
          </p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {forms.map((form) => (
            <Card key={form.id} data-testid={`intake-form-card-${form.id}`}>
              <CardContent className="p-4 flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{form.title}</p>
                  <p className="text-xs text-gray-500">
                    {(form.fields || []).filter((f) => !['heading', 'instruction'].includes(f.type)).length} questions ·{' '}
                    {form.timing === 'after' ? 'after the session' : 'before the session'}
                    {form.ask_once ? (form.once_scope === 'session_type'
                      ? ' · once per session type' : ' · once per client') : ''}
                    {form.response_count ? ` · ${form.response_count} answered` : ''}
                  </p>
                  {(form.used_by || []).length > 0 && (
                    <p className="text-xs text-gray-400 truncate" data-testid={`intake-form-usedby-${form.id}`}>
                      Attached to {form.used_by.join(', ')}
                    </p>
                  )}
                  {form.published && !(form.used_by || []).length && (
                    <p className="text-xs text-amber-600" data-testid={`intake-form-unattached-${form.id}`}>
                      Not attached to anything yet — add it under Availability → Session types
                    </p>
                  )}
                </div>
                {form.unread_count > 0 && (
                  <Badge className="bg-emerald-600 text-white shrink-0"
                         data-testid={`intake-form-unread-${form.id}`}>
                    {form.unread_count} new
                  </Badge>
                )}
                <label className="flex items-center gap-2 cursor-pointer shrink-0"
                       data-testid={`intake-form-state-${form.id}`}>
                  <Switch
                    checked={!!form.published}
                    onCheckedChange={(v) => togglePublished(form, v)}
                    data-testid={`intake-form-publish-toggle-${form.id}`}
                  />
                  <span className={`text-xs font-medium ${form.published ? 'text-emerald-600' : 'text-gray-400'}`}>
                    {form.published ? 'Live' : 'Draft'}
                  </span>
                </label>
                {form.source === 'ai' && <Badge variant="outline">From PDF</Badge>}
                <Button size="sm" variant="outline" onClick={() => openResponses(form)}
                        data-testid={`intake-form-responses-${form.id}`}>
                  Answers
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(form)}
                        data-testid={`intake-form-edit-${form.id}`}>
                  Edit
                </Button>
                <button onClick={() => remove(form)}
                        className="text-gray-400 hover:text-rose-500 transition-colors duration-200"
                        data-testid={`intake-form-delete-${form.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
